import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useInventoryTaxListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryTaxListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IInventoryState, setTaxDetails } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import TaxtActiveDeactiveComponent from "../TaxtActiveDeactiveComponent";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { FaProductHunt } from "react-icons/fa6";
import PaginationComponent from "@/app/components/ui/Pagination";
import clsx from "clsx";

const PAGE_SIZE = 10;

const tableHeader = [
  { item: "SN" },
  { item: "HSN Code" },
  { item: "CGST" },
  { item: "SGST" },
  { item: "IGST" },
  { item: "UGST" },
  { item: "Status" },
  { item: "Action" },
];

const TaxListComponent = () => {
  const { NO_PWL_RESULTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { taxList } = routeState.state as IInventoryState;
  const fetchTaxList = useInventoryTaxListAPI();

  const [currentPage, setCurrentPage] = useState<number>(1);

  const dispatch = useDispatch();

  const isTaxListLoading = useLoader("isTaxListLoadingInProgress");

  const navigate = useNavigate();

  useEffect(() => {
    fetchTaxList(null, PAGE_SIZE, currentPage);
    dispatch(setTaxDetails({ data: null }));
  }, []);

  const handelEditNavigate = (id: string) => {
    navigate(`/app/inventory/tax-add?tax_id=${id}`);
  };

  const handelSearch = (query: string) => {
    fetchTaxList(query, PAGE_SIZE, 1);
  };
  useEffect(() => {
    if (currentPage) {
      fetchTaxList(null, PAGE_SIZE, currentPage);
    }
  }, [currentPage]);

  return (
    <div className="px-6 flow-root">
      <div className=" pb-3">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => handelSearch(query)}
          containerClassName="w-full sm:w-1/2 lg:w-1/3"
          addon={<FaProductHunt className="w-4 h-4" />}
          placeholder="Search HSN Code"
        />
      </div>
      {isTaxListLoading ? (
        <TableSkeleton />
      ) : taxList?.data?.length === 0 ? (
        <NoDataFoundComponent
          imageURL={NO_PWL_RESULTS}
          message="No Service Listed"
          description="No Service  Listed, please onboard some service."
        />
      ) : (
        <div className="mt-8 flow-root">
          <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[600px]">
              <div className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          {tableHeader?.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                            >
                              {key?.item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {taxList?.data?.map((value, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === taxList?.data?.length - 1;
                          const isSingle = taxList?.data?.length === 1;

                          return (
                            <tr
                              key={value?.hsn_code}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                {(currentPage - 1) * PAGE_SIZE + (idx + 1)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">{value?.hsn_code}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {value?.cgst_percentage} %
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {value?.sgst_percentage} %
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {value?.igst_percentage} %
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {value?.ugst_percentage} %
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <TaxtActiveDeactiveComponent flage={value?.status} taxId={value?.hsn_code} />
                              </td>
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <button
                                  type="button"
                                  aria-label={`Edit ${value?.hsn_code}`}
                                  onClick={() => handelEditNavigate(value?.hsn_code)}
                                  className="text-gray-500 hover:text-black cursor-pointer"
                                >
                                  <PencilSquareIcon className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-center items-center p-4 w-full">
        <PaginationComponent
          className="pagination-bar"
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          totalCount={taxList?.total_items||0}
          onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
        />
      </div>
    </div>
  );
};
export default TaxListComponent;
